/* Storefront search results, powered by the app's all-words catalog search.
 *
 * Shopify's own search scores every product against the whole phrase, so a
 * two-word search like "ball valve" returns hundreds of valve-related items.
 * When the "Use the parts catalog search" setting is on, sections/main-search
 * .liquid mounts this element above Shopify's results and hides them. This
 * fetches GET {api}/api/storefront/search?q=…, where every word must match,
 * and renders the same product cards the Repair Parts finder uses (classes +
 * CSS from repair-parts-finder.css, add-to-cart / quick-add / add-to-quote
 * wiring identical to repair-parts-finder.js).
 *
 * Shopify's results stay in the page as the fallback: they are revealed when
 * the app finds nothing, when it cannot be reached, or when the shopper asks
 * for them via the "see everything the store search matched" link.
 */
if (!customElements.get('app-search')) {
  class AppSearch extends HTMLElement {
    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.query = (this.dataset.query || '').trim();
      this.pageSize = Number(this.dataset.pageSize) || 24;
      this.quickAddEnabled = this.dataset.quickAddEnabled === 'true';
      this.shopifyCount = Number(this.dataset.shopifyCount) || 0;
      this.moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });

      this.status = this.querySelector('[data-search-status]');
      this.grid = this.querySelector('[data-results-grid]');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.fallback = this.querySelector('[data-fallback]');
      this.shopifyResults = document.querySelector('[data-shopify-search-results]');

      this.page = 0;
      this.total = 0;
      this.loading = false;

      this.loadMoreButton.addEventListener('click', () => this.fetchPage(this.page + 1));
      this.addEventListener('click', (event) => this.handleClick(event));

      if (!this.query) {
        this.revealShopify();
        return;
      }
      this.fetchPage(1);
    }

    async fetchPage(page) {
      if (this.loading) return;
      this.loading = true;
      this.loadMoreButton.classList.add('is-loading');
      this.loadMoreButton.setAttribute('aria-disabled', 'true');
      if (page > 1) this.grid.setAttribute('aria-busy', 'true');
      try {
        const url = `${this.apiBase}/api/storefront/search?q=${encodeURIComponent(this.query)}&page=${page}&limit=${this.pageSize}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`search ${response.status}`);
        const payload = await response.json();
        if (!payload || payload.success !== true) throw new Error('search payload');
        this.page = payload.page;
        this.total = payload.total;
        this.render(payload, page > 1);
      } catch (error) {
        console.warn('[app-search] falling back to store search', error); // eslint-disable-line
        this.fail();
      } finally {
        this.loading = false;
        this.loadMoreButton.classList.remove('is-loading');
        this.loadMoreButton.removeAttribute('aria-disabled');
        this.grid.removeAttribute('aria-busy');
      }
    }

    render(payload, append) {
      const products = payload.products || [];
      const cards = products.map((product) => this.renderCard(product)).join('');
      if (append) this.grid.insertAdjacentHTML('beforeend', cards);
      else this.grid.innerHTML = cards;

      const shown = this.grid.querySelectorAll('.repair-parts-finder__item').length;
      const q = this.escapeHTML(this.query);

      if (this.total === 0) {
        // Nothing carries every word. Say so, then show what Shopify has —
        // it is loose, but it is better than a dead end.
        this.status.innerHTML = `No products match every word in “${q}”. Try fewer words, a part number, or the finders in the menu.`;
        this.loadMoreButton.hidden = true;
        this.fallback.hidden = true;
        this.revealShopify('Related results from the store search');
        return;
      }

      const noun = this.total === 1 ? 'result' : 'results';
      const progress = this.total > shown ? ` Showing ${shown} of ${this.total}.` : '';
      this.status.innerHTML = `${this.total} ${noun} for “${q}”.${progress}`;
      this.loadMoreButton.hidden = !payload.hasMore;
      // Offer Shopify's broader list only when it actually has more to show.
      this.fallback.hidden = !(this.shopifyResults && this.shopifyCount > this.total);
      const count = this.fallback.querySelector('[data-shopify-count]');
      if (count) count.textContent = String(this.shopifyCount);
    }

    fail() {
      this.status.textContent = 'Search is having trouble right now, so here are the store’s own results.';
      this.grid.innerHTML = '';
      this.loadMoreButton.hidden = true;
      this.fallback.hidden = true;
      this.revealShopify();
    }

    revealShopify(heading) {
      if (!this.shopifyResults) return;
      this.shopifyResults.hidden = false;
      const title = this.shopifyResults.querySelector('[data-shopify-results-heading]');
      if (title) {
        title.hidden = false;
        if (heading) title.textContent = heading;
      }
      this.fallback.hidden = true;
    }

    handleClick(event) {
      const reveal = event.target.closest('[data-show-shopify]');
      if (reveal) {
        event.preventDefault();
        this.revealShopify('Everything the store search matched');
        if (this.shopifyResults) this.shopifyResults.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const quote = event.target.closest('.js-add-to-quote');
      if (quote) {
        event.preventDefault();
        document.dispatchEvent(
          new CustomEvent('quote:add', {
            detail: {
              variantId: quote.dataset.variantId,
              title: quote.dataset.title,
              sku: quote.dataset.sku || null,
              image: quote.dataset.image || null,
              handle: quote.dataset.handle || null,
              quantity: 1,
            },
          })
        );
      }
    }

    /* ---- Cards: same markup and classes as repair-parts-finder.js ---- */

    renderCard(product) {
      const url = `/products/${encodeURIComponent(product.handle)}`;
      const image = product.imageUrl
        ? `<img src="${this.escapeAttribute(product.imageUrl)}" alt="${this.escapeAttribute(product.title)}" loading="lazy">`
        : '<div class="repair-parts-finder__card-placeholder" aria-hidden="true"></div>';
      const sku = product.variants.length === 1 && product.variants[0].sku
        ? `<p class="repair-parts-finder__card-sku">SKU: ${this.escapeHTML(product.variants[0].sku)}</p>`
        : '';
      // The app resolves purchasable-now / add-to-quote and the size rule;
      // never print a price for a quote-only product.
      const priceHtml = product.quoteOnly
        ? '<p class="repair-parts-finder__card-price repair-parts-finder__card-price--quote">Quote on request</p>'
        : `<p class="repair-parts-finder__card-price">${this.escapeHTML(
            product.priceMin === product.priceMax
              ? this.moneyFormatter.format(product.priceMin)
              : `From ${this.moneyFormatter.format(product.priceMin)}`
          )}</p>`;
      const chips = [
        ...(product.brand ? [product.brand] : []),
        ...(product.sizes || []).slice(0, 3).map((size) => this.displaySize(size)),
        ...(product.models || []).slice(0, 4),
      ];
      const chipsHtml = chips.length
        ? `<div class="repair-parts-finder__chips">${chips.map((chip) => `<span>${this.escapeHTML(chip)}</span>`).join('')}</div>`
        : '';

      return `
        <li class="repair-parts-finder__item">
          <div class="repair-parts-finder__card">
            <a class="repair-parts-finder__card-main" href="${this.escapeAttribute(url)}">
              <div class="repair-parts-finder__card-image">${image}</div>
              <div class="repair-parts-finder__card-content">
                ${sku}
                <h3 class="repair-parts-finder__card-title">${this.escapeHTML(product.title)}</h3>
                ${chipsHtml}
                ${priceHtml}
              </div>
            </a>
            <div class="repair-parts-finder__card-actions">
              ${this.renderCardAction(product, url)}
              <a class="repair-parts-finder__card-link" href="${this.escapeAttribute(url)}">View product</a>
            </div>
          </div>
        </li>
      `;
    }

    renderCardAction(product, url) {
      if (product.quoteOnly) {
        if (product.variants.length === 1) {
          const v = product.variants[0];
          return `
            <button type="button" class="btn btn--primary w-full js-add-to-quote"
              data-variant-id="${this.escapeAttribute(v.variantId)}"
              data-title="${this.escapeAttribute(product.title)}"
              data-sku="${this.escapeAttribute(v.sku || '')}"
              data-image="${this.escapeAttribute(product.imageUrl || '')}"
              data-handle="${this.escapeAttribute(product.handle)}">
              Add to quote
            </button>
          `;
        }
        return `<a class="btn btn--primary w-full" href="${this.escapeAttribute(url)}">Choose options to quote</a>`;
      }

      if (!product.available) {
        return '<button class="btn btn--primary w-full" type="button" disabled>Out of stock</button>';
      }

      if (product.variants.length === 1) {
        const cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
        // Minimal markup product-form.js needs: a .js-product-form form with a
        // variant id and an [name="add"] button. It AJAX-adds the item, updates
        // the cart icon, and opens the theme's cart drawer.
        return `
          <product-form>
            <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
            <form method="post" action="${this.escapeAttribute(cartAddUrl)}" class="js-product-form" novalidate>
              <input type="hidden" name="id" value="${this.escapeAttribute(product.variants[0].variantId)}">
              <button class="btn btn--primary w-full" name="add" aria-haspopup="dialog">Add to cart</button>
            </form>
          </product-form>
        `;
      }

      if (this.quickAddEnabled) {
        // Multi-variant: the theme's quick-add drawer picks up .js-quick-add
        // buttons through a delegated document click handler.
        return `
          <button type="button"
                  class="btn btn--primary w-full js-quick-add"
                  aria-haspopup="dialog"
                  data-product-url="${this.escapeAttribute(url)}"
                  data-product-default-variant="false">
            Choose options
          </button>
        `;
      }

      return `<a class="btn btn--primary w-full" href="${this.escapeAttribute(url)}">Choose options</a>`;
    }

    /** Sizes as fractions (1 ½", not 1.5") — display only; filtering keeps the
        API's value. Mirrors the app's src/lib/size-display.ts. Null = not a size. */
    fractionSize(size) {
      const s = String(size).replace(/["”″]/g, '').trim();
      let v = null;
      const mixed = s.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
      const frac = s.match(/^(\d+)\/(\d+)$/);
      if (mixed && Number(mixed[3])) v = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      else if (frac && Number(frac[2])) v = Number(frac[1]) / Number(frac[2]);
      else if (/^(\d+\.?\d*|\.\d+)$/.test(s)) v = Number(s);
      if (v === null || !Number.isFinite(v)) return null;
      const n = Math.round(v * 16);
      if (Math.abs(v * 16 - n) > 0.05) return s;
      const whole = Math.floor(n / 16);
      const rest = n % 16;
      if (!rest) return String(whole);
      const gcd = (a, b) => (b ? gcd(b, a % b) : a);
      const d = gcd(rest, 16);
      const key = `${rest / d}/${16 / d}`;
      const glyph = { '1/2': '½', '1/4': '¼', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞' }[key] || key;
      return whole ? `${whole} ${glyph}` : glyph;
    }

    /** Canonical fractions ("1 1/2") shown as 1 ½" with an inch mark. */
    displaySize(size) {
      const f = this.fractionSize(size);
      return f === null ? size : `${f}"`;
    }

    escapeHTML(value) {
      return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[char]));
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }
  }

  customElements.define('app-search', AppSearch);
}
